import { useCallback, useMemo, useState } from 'react';

interface UseFilterDrawerOptions<T> {
  value: T;
  onConfirm: (value: T) => void;
  isEmpty: (value: T) => boolean;
  emptyValue: T;
}

interface UseFilterDrawerReturn<T> {
  draftValue: T;
  setDraftValue: React.Dispatch<React.SetStateAction<T>>;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  confirm: () => void;
  reset: () => void;
  hasActiveFilters: boolean;
}

export function useFilterDrawer<T>({
  value,
  onConfirm,
  isEmpty,
  emptyValue,
}: UseFilterDrawerOptions<T>): UseFilterDrawerReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<T>(value);

  const open = useCallback(() => {
    setDraftValue(structuredClone(value));
    setIsOpen(true);
  }, [value]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const confirm = useCallback(() => {
    onConfirm(draftValue);
    setIsOpen(false);
  }, [draftValue, onConfirm]);

  const reset = useCallback(() => {
    setDraftValue(emptyValue);
  }, [emptyValue]);

  const hasActiveFilters = useMemo(() => !isEmpty(draftValue), [isEmpty, draftValue]);

  return {
    draftValue,
    setDraftValue,
    isOpen,
    open,
    close,
    confirm,
    reset,
    hasActiveFilters,
  };
}
